import * as React from 'react'

export type SocialPageType = 'website' | 'article' | 'profile'

export interface SocialMetaProps {
  // Drives `og:title`. The page keeps its own `<title>` element (next/head handles `<title>` specially),
  // so this helper intentionally does not emit one — it only mirrors the value into Open Graph.
  title?: string
  description?: string
  // Emits `<link rel="canonical">` and (unless `ogUrl` is given) `og:url`. Ignored as a canonical when
  // `noIndex` is set: a `?status=success` render canonicalized to its query-stripped form page could
  // carry the noindex over to that indexable URL. The value still feeds `og:url`.
  canonicalUrl?: string
  // Overrides `og:url` when it must differ from the canonical — e.g. a page that declares no canonical
  // (or a `noIndex` share page) but still wants an OG url pointing at the shareable/app link.
  ogUrl?: string
  imageUrl?: string
  imageAlt?: string
  // Pixel dimensions of `imageUrl`. Both are required to emit either, since a scraper that trusts
  // one and infers the other would lay the card out wrong. Supply them only for images whose size
  // is known at build time — never guess for user-supplied images.
  imageWidth?: number
  imageHeight?: number
  type?: SocialPageType
  siteName?: string
  // Emits `<meta name="robots" content="noindex, follow">` — for pages that should render a social
  // preview but stay out of search indexes (e.g. login share previews).
  noIndex?: boolean
}

// The shared SEO / social-preview meta block for every Notability web property.
// Returns an array of tags to spread directly into a site's own next/head `<Head>` to allow
// the key-based de-duplication built into `next/head`, which only works on direct children.
export function socialMetaTags({
  title,
  description,
  canonicalUrl,
  ogUrl,
  imageUrl,
  imageAlt,
  imageWidth,
  imageHeight,
  type = 'website',
  siteName = 'Notability',
  noIndex = false
}: SocialMetaProps): React.ReactElement[] {
  const resolvedOgUrl = ogUrl ?? canonicalUrl
  const tags: React.ReactElement[] = []
  if (noIndex) {
    tags.push(<meta key="robots" name="robots" content="noindex, follow" />)
  }
  if (description) {
    tags.push(<meta key="description" name="description" content={description} />)
  }
  if (canonicalUrl && !noIndex) {
    tags.push(<link key="canonical" rel="canonical" href={canonicalUrl} />)
  }
  tags.push(<meta key="og:site_name" property="og:site_name" content={siteName} />)
  tags.push(<meta key="og:type" property="og:type" content={type} />)
  if (title) {
    tags.push(<meta key="og:title" property="og:title" content={title} />)
  }
  if (description) {
    tags.push(<meta key="og:description" property="og:description" content={description} />)
  }
  if (resolvedOgUrl) {
    tags.push(<meta key="og:url" property="og:url" content={resolvedOgUrl} />)
  }
  if (imageUrl) {
    tags.push(<meta key="og:image" property="og:image" content={imageUrl} />)
    if (imageAlt) {
      tags.push(<meta key="og:image:alt" property="og:image:alt" content={imageAlt} />)
    }
    if (imageWidth && imageHeight) {
      tags.push(<meta key="og:image:width" property="og:image:width" content={String(imageWidth)} />)
      tags.push(<meta key="og:image:height" property="og:image:height" content={String(imageHeight)} />)
    }
  }
  tags.push(<meta key="twitter:card" name="twitter:card" content="summary_large_image" />)
  return tags
}
